import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { MessageSquare, Users, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { WebOnly } from './ui/WebOnly';
import { useUserData } from './UserDataContext';
import { PATHS } from '../router/paths';
import { formatMemberCount } from './utils/memberCount';
import { MiniDiscoverCard } from './ui/MiniDiscoverCard';
import { stashPendingGroupInvite, buildGroupScreenPath } from '../utils/inviteCode';

const APP_STORE_URL = 'https://apps.apple.com/app/apple-store/id6757343172?pt=128420251&ct=ForumInvite&mt=8';

const isProfileComplete = (profile: any): boolean => {
    if (!profile) return false;
    if (profile.setupComplete === true) return true;
    // Authoritative when false too — see ProtectedRoute. Without this the
    // leniency check below releases a half-finished signup.
    if (profile.setupComplete === false) return false;
    if (['legion', 'union', 'watcher', 'verified'].includes(profile.rank)) return true;
    const hasPhotos = Array.isArray(profile.portfolio) && profile.portfolio.some((p: any) => p && String(p).trim() !== '');
    const hasName = !!(profile.name && profile.name.trim());
    const hasUsername = !!(profile.username && profile.username.trim());
    return hasUsername && hasName && hasPhotos;
};

/**
 * Public landing page for a shared forum link (/forums/:forumId?invite=CODE).
 *
 * The sibling of TribeInvitePage, and it exists for the same reason: a shared
 * link has to survive being opened by someone with no account, in a browser,
 * outside the app. Forums are openly joinable, so the code in the link only
 * decides the copy here (invited vs discovering) rather than gating anything.
 */
export function ForumInvitePage() {
    const { forumId } = useParams<{ forumId: string }>();
    const [searchParams] = useSearchParams();
    const inviteCode = searchParams.get('invite') || '';
    const navigate = useNavigate();
    const { firebaseUser, userProfile, authLoading } = useUserData();

    const [forumData, setForumData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [inviteValid, setInviteValid] = useState(false);
    // Showcase strip — a random sample of real public profiles. Read via a
    // Cloud Function because unauthenticated visitors can't query users directly.
    const [showcaseUsers, setShowcaseUsers] = useState<any[]>([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const { getApp } = await import('firebase/app');
                const fn = httpsCallable(getFunctions(getApp()), 'getPublicUserSample');
                const res: any = await fn({});
                const users = Array.isArray(res?.data?.users) ? res.data.users : [];
                if (!cancelled) setShowcaseUsers(users);
            } catch (e) {
                // Hide the section on any failure rather than showing an empty strip.
                if (!cancelled) setShowcaseUsers([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Logged-in + complete users SKIP the public page entirely and go straight to
    // the in-app forum screen. Don't wait for the forum fetch — redirect as soon
    // as auth is known.
    useEffect(() => {
        if (authLoading) return;
        if (firebaseUser && isProfileComplete(userProfile)) {
            navigate(buildGroupScreenPath('forum', String(forumId), inviteCode), { replace: true });
        }
    }, [authLoading, firebaseUser, userProfile, forumId, inviteCode, navigate]);

    useEffect(() => {
        if (!forumId) return;
        (async () => {
            try {
                const { getDbSafe } = await import('../services/firebaseConfig');
                const { doc, getDoc } = await import('firebase/firestore');
                const db = await getDbSafe();
                const snap = await getDoc(doc(db, 'forums', forumId));
                if (snap.exists()) {
                    const data: any = { id: snap.id, ...snap.data() };
                    setForumData(data);
                    setInviteValid(!!(inviteCode && data.inviteCode === inviteCode));
                }
            } catch (e) {
                console.error('[ForumInvitePage] fetch failed:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [forumId, inviteCode]);

    const handleLogin = () => {
        if (forumId) stashPendingGroupInvite('forum', forumId, inviteCode);
        navigate(PATHS.LOGIN, {
            state: { from: { pathname: `/forum/${forumId}`, search: inviteCode ? `?invite=${inviteCode}` : '' } },
        });
    };

    const handleSignup = () => {
        if (forumId) stashPendingGroupInvite('forum', forumId, inviteCode);
        navigate(PATHS.SIGNUP);
    };

    // Logged-in users are being redirected to the in-app forum screen — render
    // nothing (no public-page flash, no loading spinner) while that happens.
    if (!authLoading && firebaseUser && isProfileComplete(userProfile)) return null;

    if (authLoading || loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center" style={{ backgroundColor: '#0d1b2a' }}>
                <Loader2 className="w-8 h-8 animate-spin text-white/40" />
            </div>
        );
    }

    if (!forumData) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 text-white p-6 text-center" style={{ backgroundColor: '#0d1b2a' }}>
                <MessageSquare className="w-12 h-12 text-white/20" />
                <p className="text-lg font-bold">Forum not found</p>
                <p className="text-sm text-white/50">This invite link may have expired or the forum may have been removed.</p>
            </div>
        );
    }

    const forumName = forumData.name || forumData.title || 'Forum';
    const bannerUrl = forumData.bannerUrl || forumData.bannerImage || forumData.image || '';

    return (
        <div
            className="min-h-screen flex flex-col items-center"
            style={{
                backgroundColor: '#0d1b2a',
                // Subtle AfroYap kente-style crosshatch (gold + red) over dark navy.
                // Inline so it renders regardless of theme class / specificity.
                backgroundImage: `
                    repeating-linear-gradient(45deg, rgba(243,156,18,0.06) 0px, rgba(243,156,18,0.06) 2px, transparent 2px, transparent 22px),
                    repeating-linear-gradient(-45deg, rgba(231,76,60,0.05) 0px, rgba(231,76,60,0.05) 2px, transparent 2px, transparent 22px)
                `,
                backgroundAttachment: 'fixed',
            }}
        >
            {/* Banner */}
            <div className="relative w-full max-w-lg aspect-[16/7] bg-[#1a1a1a] shrink-0">
                {bannerUrl ? (
                    <img src={bannerUrl} className="w-full h-full object-cover" alt="" />
                ) : (
                    <div
                        className="w-full h-full"
                        style={{ background: `linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #8e24aa 100%)` }}
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Forum name overlay */}
                <div className="absolute bottom-0 inset-x-0 p-4">
                    <h1 className="text-2xl font-black text-white leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                        {forumName}
                    </h1>
                </div>
            </div>

            {/* Card body — 24px between each major section */}
            <div className="w-full max-w-lg px-6 py-6 flex flex-col gap-6">
                {/* Member count + description block */}
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 text-sm text-white/60">
                        <span className="flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            {formatMemberCount(forumData.memberCount || 1)}
                        </span>
                        {typeof forumData.postCount === 'number' && forumData.postCount > 0 && (
                            <span className="flex items-center gap-1.5">
                                <MessageSquare className="w-3.5 h-3.5" />
                                {forumData.postCount} {forumData.postCount === 1 ? 'post' : 'posts'}
                            </span>
                        )}
                    </div>
                    {forumData.description && (
                        <p className="text-sm text-white/70 leading-relaxed line-clamp-3">
                            {forumData.description}
                        </p>
                    )}
                </div>

                {/* Invite context — 16px internal padding */}
                {inviteValid && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20"
                    >
                        <p className="text-sm text-blue-300 font-medium">
                            You have been invited to join {forumName}.
                        </p>
                        <p className="text-xs text-blue-400/70 mt-0.5">
                            Sign up or log in to join the conversation.
                        </p>
                    </motion.div>
                )}

                {/* Join CTA heading */}
                <h2 className="text-base font-bold text-white text-center">
                    {inviteValid ? `Join ${forumName}` : `Discover ${forumName}`} on AfroYap
                </h2>

                {/* App Store button — 14px vertical padding so it reads as a button */}
                <WebOnly>
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="block w-full">
                    <Button className="w-full h-auto py-[14px] bg-white hover:bg-white/90 text-black font-bold rounded-xl flex items-center justify-center gap-3 text-base">
                        <svg viewBox="0 0 384 512" className="w-5 h-5 fill-current shrink-0">
                            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 33-17.9 64.4-17.9 31.7 0 38.3 17.9 64.2 17.4 48.5-.7 85.7-82.2 102.6-119.3-22.6-14.9-42.1-39.6-43-69.6zM263.1 81.2c15.5-18.5 25.5-44.9 22.2-71.2-22.6 1.1-49.6 15.2-65.8 34-14.6 16.6-27.3 42.7-23.4 68.2 25.2 2 49.5-13.5 67-31z"/>
                        </svg>
                        Download on the App Store
                    </Button>
                </a>
                </WebOnly>

                {/* Create Account */}
                <Button
                    onClick={handleSignup}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm"
                >
                    Create Account
                </Button>

                {/* Already have an account */}
                <button
                    onClick={handleLogin}
                    className="text-sm text-white/50 hover:text-white/80 transition-colors text-center"
                >
                    Already have an account? <span className="text-blue-400 font-semibold">Log in</span>
                </button>
            </div>

            {/* Community showcase — auto-scrolling conveyor of real public profiles,
                styled exactly like the Discover "Newest Yappers" cards (full-bleed
                photo + rotated flag sticker, no text). Hidden when the sample is empty. */}
            {showcaseUsers.length > 0 && (
                <div className="w-full max-w-lg">
                    <h3
                        className="text-white text-center font-semibold"
                        style={{ fontSize: 16, marginTop: 20, marginBottom: 12 }}
                    >
                        Join thousands of Africans &amp; the diaspora
                    </h3>
                    {/* overflow-hidden masks the horizontal scroll; generous vertical
                        padding gives the rotated flag sticker room above each card. */}
                    <div className="w-full overflow-hidden" style={{ paddingTop: 24, paddingBottom: 24 }}>
                        {/* CSS keyframe track (see .tribe-carousel-track in index.css).
                            Array is duplicated and each card carries its full stride as
                            margin-right, so translateX(-50%) loops seamlessly. */}
                        <div className="tribe-carousel-track" style={{ display: 'flex', width: 'max-content' }}>
                            {[...showcaseUsers, ...showcaseUsers].map((u, i) => (
                                <div
                                    key={i}
                                    style={{ flexShrink: 0, overflow: 'visible', paddingTop: 12, marginRight: 12 }}
                                >
                                    <div style={{ width: 150, aspectRatio: '3 / 4' }}>
                                        <MiniDiscoverCard user={u as any} images={u.photo ? [u.photo] : []} intervalMs={3000} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* AfroYap tagline — below the carousel */}
            <p className="w-full max-w-lg px-6 pb-8 text-center text-[11px] text-white/20">
                AfroYap · The Afro-Diasporic Social Network
            </p>
        </div>
    );
}

export default ForumInvitePage;
